import React, { useState, useMemo } from 'react';
import { Plus, Search, Filter, MoreHorizontal, Tag, TrendingUp, TrendingDown } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger,
  DropdownMenuSeparator 
} from '../ui/dropdown-menu';
import { CategoryTable } from './CategoryTable';
import { CategoryForm } from './CategoryForm';
import { CategoryFilters } from './CategoryFilters';
import {
  Category,
  CategoryType,
  CategoryStatus,
  formatCategoryType
} from '../../types/category';
import { useCategories } from '../../hooks/useCategories';
import { categorySeedingService } from '../../services/categorySeedingService';

export function CategoryPage() {
  const { categories, loading, error, refetch } = useCategories();
  const [selectedCategories, setSelectedCategories] = useState<Set<number>>(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [filters, setFilters] = useState({});

  // Calculate summary from real data
  const summary = useMemo(() => {
    const incomeCategories = categories.filter(c => c.type === CategoryType.INCOME);
    const expenseCategories = categories.filter(c => c.type === CategoryType.EXPENSE);
    const activeCategories = categories.filter(c => c.is_active !== false);
    const inactiveCategories = categories.filter(c => c.is_active === false);

    return {
      total: categories.length,
      by_type: {
        [CategoryType.INCOME]: incomeCategories.length,
        [CategoryType.EXPENSE]: expenseCategories.length
      },
      by_status: {
        [CategoryStatus.ACTIVE]: activeCategories.length,
        [CategoryStatus.INACTIVE]: inactiveCategories.length
      },
      most_used: categories.slice(0, 5),
      least_used: categories.slice(-5).reverse()
    };
  }, [categories]);

  const filteredCategories = useMemo(() => {
    let filtered = [...categories];

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(category =>
        category.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        category.description?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Sort by name
    filtered.sort((a, b) => a.name.localeCompare(b.name));

    return filtered;
  }, [categories, searchTerm]);

  const handleSelectCategory = (categoryId: string, selected: boolean) => {
    const newSelected = new Set(selectedCategories);
    if (selected) {
      newSelected.add(categoryId);
    } else {
      newSelected.delete(categoryId);
    }
    setSelectedCategories(newSelected);
  };

  const handleSelectAll = (selected: boolean) => {
    if (selected) {
      setSelectedCategories(new Set(filteredCategories.map(c => c.id)));
    } else {
      setSelectedCategories(new Set());
    }
  };

  const handleBulkAction = (action: string) => {
    console.log(`Executing ${action} on categories:`, Array.from(selectedCategories));
    // Here you would implement the actual bulk actions
    setSelectedCategories(new Set());
  };

  const handleFormSubmit = () => {
    setShowForm(false);
    setEditingCategory(null);
    // Force refetch to ensure categories are updated
    setTimeout(() => {
      refetch();
    }, 200);
  };

  const handleEdit = (category: Category) => {
    setEditingCategory(category);
    setShowForm(true);
  };

  const handleDelete = () => {
    // Force refetch to ensure categories are updated
    setTimeout(() => {
      refetch();
    }, 200);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground">Carregando categorias...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <p className="text-destructive mb-2">Erro ao carregar categorias</p>
          <p className="text-muted-foreground text-sm">{error}</p>
          <Button onClick={refetch} className="mt-4">
            Tentar novamente
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with summary cards */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Categorias</h1>
            <p className="text-muted-foreground mt-1">
              Gerencie as categorias de receitas e despesas
            </p>
          </div>
          <Button onClick={() => setShowForm(true)} className="gap-2">
            <Plus className="h-4 w-4" />
            Nova Categoria
          </Button>
        </div>

        {/* Summary Cards */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <Card className="relative overflow-hidden group hover:shadow-lg transition-all duration-300">
            <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 to-blue-600/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 relative">
              <CardTitle className="text-sm font-medium">Total de Categorias</CardTitle>
              <Tag className="h-4 w-4 text-blue-600 dark:text-blue-400" />
            </CardHeader>
            <CardContent className="relative">
              <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">{summary.total}</div>
              <p className="text-xs text-muted-foreground">
                {summary.by_status[CategoryStatus.ACTIVE]} ativas
              </p>
            </CardContent>
          </Card>

          <Card className="relative overflow-hidden group hover:shadow-lg transition-all duration-300">
            <div className="absolute inset-0 bg-gradient-to-br from-green-500/10 to-green-600/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 relative">
              <CardTitle className="text-sm font-medium">Receitas</CardTitle>
              <TrendingUp className="h-4 w-4 text-finance-green" />
            </CardHeader>
            <CardContent className="relative">
              <div className="text-2xl font-bold text-finance-green">
                {summary.by_type[CategoryType.INCOME]}
              </div>
              <p className="text-xs text-muted-foreground">
                {((summary.by_type[CategoryType.INCOME] / summary.total) * 100).toFixed(1)}% do total
              </p>
            </CardContent>
          </Card>

          <Card className="relative overflow-hidden group hover:shadow-lg transition-all duration-300">
            <div className="absolute inset-0 bg-gradient-to-br from-red-500/10 to-red-600/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 relative">
              <CardTitle className="text-sm font-medium">Despesas</CardTitle>
              <TrendingDown className="h-4 w-4 text-finance-red" />
            </CardHeader>
            <CardContent className="relative">
              <div className="text-2xl font-bold text-finance-red">
                {summary.by_type[CategoryType.EXPENSE]}
              </div>
              <p className="text-xs text-muted-foreground">
                {((summary.by_type[CategoryType.EXPENSE] / summary.total) * 100).toFixed(1)}% do total
              </p>
            </CardContent>
          </Card>

          <Card className="relative overflow-hidden group hover:shadow-lg transition-all duration-300">
            <div className="absolute inset-0 bg-gradient-to-br from-purple-500/10 to-purple-600/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 relative">
              <CardTitle className="text-sm font-medium">Mais Usada</CardTitle>
              <Tag className="h-4 w-4 text-purple-600 dark:text-purple-400" />
            </CardHeader>
            <CardContent className="relative">
              <div className="text-2xl font-bold text-purple-600 dark:text-purple-400 truncate">
                {summary.most_used[0]?.name || 'N/A'}
              </div>
              <p className="text-xs text-muted-foreground">
                {summary.most_used[0]?.usage_count || 0} usos
              </p>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Search and Filters */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Lista de Categorias</CardTitle>
              <CardDescription>
                {filteredCategories.length} de {categories.length} categorias
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              {selectedCategories.size > 0 && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm">
                      <MoreHorizontal className="h-4 w-4" />
                      Ações ({selectedCategories.size})
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => handleBulkAction('activate')}>
                      Ativar selecionadas
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => handleBulkAction('deactivate')}>
                      Desativar selecionadas
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem 
                      onClick={() => handleBulkAction('delete')}
                      className="text-destructive"
                    >
                      Excluir selecionadas
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowFilters(!showFilters)}
                className={showFilters ? 'bg-muted' : ''}
              >
                <Filter className="h-4 w-4" />
                Filtros
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
              <Input
                placeholder="Buscar categorias..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            {searchTerm && (
              <Badge variant="secondary" className="gap-1">
                {filteredCategories.length} resultado{filteredCategories.length !== 1 ? 's' : ''}
              </Badge>
            )}
          </div>

          {showFilters && (
            <CategoryFilters
              filters={filters}
              onFiltersChange={setFilters}
            />
          )}

          <CategoryTable
            categories={filteredCategories}
            selectedCategories={selectedCategories}
            onSelectCategory={handleSelectCategory}
            onSelectAll={handleSelectAll}
            onEdit={handleEdit}
            onDelete={handleDelete}
          />
        </CardContent>
      </Card>

      {/* Form Dialog */}
      {showForm && (
        <CategoryForm
          category={editingCategory}
          open={showForm}
          onOpenChange={setShowForm}
          onSuccess={handleFormSubmit}
        />
      )}
    </div>
  );
}